import { CheckCircle, Info } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { AttentionCard } from './AttentionCard'
import type { TodayBoardItem, SeveritySummary } from './useTodayBoardData'

interface AttentionColumnProps {
  items: TodayBoardItem[]
  severitySummary: SeveritySummary
  isToday: boolean
}

function SeverityBadges({ summary }: { summary: SeveritySummary }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {summary.critical > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 dark:bg-red-950/40 px-2 py-0.5 text-[10px] text-red-700 dark:text-red-300 font-medium">
          {summary.critical} critical
        </span>
      )}
      {summary.warning > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-950/40 px-2 py-0.5 text-[10px] text-amber-700 dark:text-amber-300 font-medium">
          {summary.warning} warning{summary.warning > 1 ? 's' : ''}
        </span>
      )}
    </div>
  )
}

export function AttentionColumn({ items, severitySummary, isToday }: AttentionColumnProps) {
  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Severity badges — inline summary (no duplicate heading) */}
      {isToday && items.length > 0 && (
        <div className="shrink-0 pb-3">
          <SeverityBadges summary={severitySummary} />
        </div>
      )}

      {/* Scrollable content */}
      <ScrollArea className="flex-1 min-h-0">
        {!isToday ? (
          <div className="flex items-start gap-2 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 px-3 py-2.5 text-xs text-blue-700 dark:text-blue-300">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>Attention feed is only shown for today. Switch to today to see items needing action.</span>
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-center gap-2 rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/20 px-3 py-4 text-green-700 dark:text-green-300">
            <CheckCircle className="h-4 w-4 shrink-0" />
            <span className="text-sm font-medium">All clear</span>
          </div>
        ) : (
          <div className="space-y-2 pr-2">
            {items.map((item) => (
              <AttentionCard key={item.allocationId} item={item} />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  )
}
